import {AfterViewInit, Component, ElementRef, Input, ViewChild} from '@angular/core';
import {MatPaginator, MatSort} from '@angular/material';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/map';
import {BaseListComponent} from '../../SharedModule/BaseListComponent/base-list.component';
import {SnackService} from '../../services/snack.service';
import {DialogService} from '../../services/dialog.service';
import {Router} from '@angular/router';
import {merge} from 'rxjs/observable/merge';
import {SortInfo} from '../../model/Helpers/SortInfo';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import {Filter} from '../../model/Helpers/Filter';
import {Pagination} from '../../model/Helpers/Pagination';
import {ContractService} from '../../services/contract.service';
import {ContractsDataSource} from '../../model/DataSources/ContractsDataSource';
import {Contract} from '../../model/Entities/Contract';
import {ContractState} from '../../model/Entities/ContractState';
import {PrintService} from '../../services/print.service';
import {ContractEvent} from '../../model/Entities/ContractEvent';
import {ContractEventService} from '../../services/event.service';
import {UserDeleteConfirmDialogComponent} from '../../UsersModule/UserDeleteConfirmDialogComponent/user-delete-confirm.dialog.component';
import {User} from '../../model/Entities/User';
import {ContractDeleteConfirmDialogComponent} from '../ContractDeleteConfirmDialogComponent/contract-delete-confirm.dialog.component';
import {ContractStateComponent} from '../../SharedModule/ContractStateComponent/contract-state.component';

@Component({
    selector: 'contracts-list',
    templateUrl: './contracts-list.component.html',
    styleUrls: ['./contracts-list.component.scss']
})

export class ContractsListComponent extends BaseListComponent implements AfterViewInit {
    tableColumns: String[] = ['id', 'customer', 'term', 'price', 'deliveryPaymentInfo', 'actions'];
    contractStateClass: any = ContractState;

    totalLost: number;

    defaultFilters = {
        from: null,
        to: null,
        delivery: 'all',
        eshop: 'all',
        available: 'all',
        state: [],
        eventId: null
    };

    filters = {
        from: null,
        to: null,
        delivery: 'all',
        eshop: 'all',
        available: 'all',
        state: [],
        eventId: null
    };

    contractEvents: ContractEvent[] = [];

    @Input() customerId = null;
    @Input() productId = null;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('search') search: ElementRef;

    constructor(private contractService: ContractService,
                private contractEventService: ContractEventService,
                private snackService: SnackService,
                private dialogService: DialogService,
                public printService: PrintService,
                private router: Router) {
        super();

        this.dataSource = new ContractsDataSource(this.contractService);
    }

    ngAfterViewInit() {
        this.prepareDataLoading();
        this.reloadPage();
        this.loadContractEvents();

        // If the user changes the sort order, reset back to the first page.
        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    }

    prepareDataLoading(): void {
        merge(
            this.paginator.page,
            this.sort.sortChange,
            Observable.fromEvent(this.search.nativeElement, 'keyup').debounceTime(150).distinctUntilChanged(),
            this.dataChangedEventSubject
        ).subscribe(() => {
            if (!this.dataSource) {
                return;
            }

            const params = {
                expand: 'customer,items,lost_items',
                summary: '',
            };

            if (this.productId || this.customerId) {
                params.summary = 'total_loss_pcs';
            }

            this.dataSource.loadPage(
                new Pagination(this.paginator.pageSize, this.paginator.pageSize * this.paginator.pageIndex),
                new SortInfo(this.sort.active, this.sort.direction), this.getFilters(), params);
        });
    }

    public resetFilters(): void {
        if (this.search) {
            this.search.nativeElement.value = '';
        }
        super.resetFilters();
    }

    public getFilters(): Filter[] {
        const f = [];

        if (this.customerId !== null) {
            f.push(new Filter('customer_id', this.customerId));
        }

        if (this.productId !== null) {
            f.push(new Filter('product_id', this.productId));
        }

        if (this.filters.delivery !== 'all') {
            if (this.filters.delivery) {
                f.push(new Filter('delivery', 1));
            } else {
                f.push(new Filter('delivery', 0));
            }
        }

        if (this.filters.eshop !== 'all' && this.filters.eshop) {
            f.push(new Filter('eshop', this.filters.eshop));
        }

        if (this.filters.state && this.filters.state.length > 0) {
            f.push(new Filter('state', this.filters.state.join(',')));
        }

        if (this.filters.eventId !== null && this.filters.eventId) {
            f.push(new Filter('event_id', this.filters.eventId));
        }

        if (this.search.nativeElement.value.trim().length > 0) {
            f.push(new Filter('search', this.search.nativeElement.value));
        }

        if (typeof this.filters.available !== 'undefined' && this.filters.available !== 'all' && this.filters.from && this.filters.to) {
            f.push(new Filter('from', this.filters.from.toISOString()));
            f.push(new Filter('to', this.filters.to.toISOString()));
            f.push(new Filter('unavailable', !this.filters.available));
        } else {
            if (this.filters.from !== null && this.filters.from) {
                f.push(new Filter('to_end', this.filters.from.toISOString(), '>='));
            }

            if (this.filters.to !== null && this.filters.to) {
                f.push(new Filter('from_start', this.filters.to.toISOString(), '<='));
            }
        }

        return f;
    }

    public getReportFilters(): Filter[] {
        const f = [];

        if (!this.checkContractReportPrintValidity()) {
            return f;
        }

        f.push(new Filter('from', this.filters.from.toISOString()));
        f.push(new Filter('to', this.filters.to.toISOString()));

        if (this.filters.state && this.filters.state.length > 0) {
            f.push(new Filter('states', this.filters.state.join(',')));
        }

        if (this.customerId !== null) {
            f.push(new Filter('customer_id', this.customerId));
        }

        if (this.productId !== null) {
            f.push(new Filter('product_id', this.productId));
        }

        return f;
    }

    getTotalLoss(): number {
        return this.dataSource.getSummary().total_loss_pcs;
    }

    showContractDetail(contract: Contract): void {
        this.router.navigate([`/contract/${contract.id}`]);
    }

    checkContractReportPrintValidity(): boolean {
        return this.filters.from && this.filters.to;
    }

    loadContractEvents(): void {
        this.contractEventService.getSimpleList().subscribe((events: ContractEvent[]) => {
            this.contractEvents = events;
        });
    }

    delete(contract: Contract): void {
        this.dialogService.showDialog(ContractDeleteConfirmDialogComponent, {
            'contract': contract
        }).afterClosed().subscribe((result) => {
            if (result) {
                this.contractService.delete(contract.id).subscribe(() => {
                    this.snackService.showSuccess(`Zápůjčka ${contract.id} byla smazána`);
                    this.reloadPage();
                });
            }
        });
    }

}




// WEBPACK FOOTER //
// /app/src/ContractsModule/ContractsListComponent/contracts-list.component.ts